import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Typography, Box, TextField,
  alpha, InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Assignment as RequirementIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Flag as FlagIcon,
  Notes as NotesIcon,
  DriveFileRenameOutline as NameIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import SectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import SearchableSelect from '../../common/SearchableSelect';
import { inputRootSx } from '../../common/formFieldSx';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canAccessPartnershipManagement,
  canEditRequirement,
  isRequirementOwner,
} from '../../../utils/partnershipPermissions';
import {
  createFieldErrorGate,
  formatFieldErrorsMessage,
  getFirstErrorMessage,
  rejectConfirmValidation,
  applyApiValidationErrors,
} from '../../../utils/formValidationFeedback';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const ACCENT         = '#38BDF8';
const NEON3          = '#34D399';
const DANGER         = '#F87171';
const TEXT_PRIMARY   = '#E2E8F0';
const TEXT_SECONDARY = '#94A3B8';
const SURFACE_BASE   = '#050d1a';
const SURFACE_MID    = '#0a1628';
const SURFACE_UP     = '#0f1f38';

const F = ({ sx, ...props }) => <TextField {...props} sx={{ ...inputRootSx, ...sx }} />;

const iconAdorn = (Icon) => (
  <InputAdornment position="start"><Icon sx={{ color: alpha(ACCENT, 0.4), fontSize: 18 }} /></InputAdornment>
);
const topAdorn = (Icon) => (
  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
    <Icon sx={{ color: alpha(ACCENT, 0.4), fontSize: 18 }} />
  </InputAdornment>
);

// ─── Options ──────────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not started' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'in_contact',  label: 'In contact'  },
  { value: 'completed',   label: 'Completed'   },
  { value: 'rejected',    label: 'Rejected'    },
  { value: 'cancelled',   label: 'Cancelled'   },
];

const STATUS_VALUES = STATUS_OPTIONS.map((o) => o.value);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const PHONE_PATTERN = /^[\d\-+()\s]+$/;
const URL_RE = /^(https?:\/\/)?([\w.-]+\.)+[\w.-]{2,}(\/[^\s]*)?$/i;

const CUSTOM_COMPANY_VALUE = '__custom__';

const MANUAL_COMPANY_FIELDS = [
  'company_name', 'company_address', 'company_contact',
  'company_contact_email', 'company_website',
];

const API_VALIDATION_FIELDS = new Set([
  'wanted_item', 'amount', 'purpose', 'company_id',
  ...MANUAL_COMPANY_FIELDS, 'status', 'user_ids',
]);

const validateRequirementField = (name, value) => {
  const v = typeof value === 'string' ? value.trim() : value;
  switch (name) {
    case 'wanted_item':
      if (!v) return 'Item / resource is required.';
      return '';
    case 'amount':
      if (!v) return 'Amount / quantity is required.';
      if (!/^[0-9\s.,]+$/.test(v)) return 'Amount must be a number.';
      return '';
    case 'purpose':
      if (!v) return 'Purpose is required.';
      return '';
    case 'company_name':
      if (!v) return 'Company name is required.';
      return '';
    case 'company_address':
      if (!v) return 'Address is required.';
      return '';
    case 'company_contact':
      if (!v) return 'Contact number is required.';
      if (!PHONE_PATTERN.test(v)) return 'Invalid phone number.';
      return '';
    case 'company_contact_email':
      if (!v) return 'Contact email is required.';
      if (!EMAIL_RE.test(v)) return 'Enter a valid email address.';
      return '';
    case 'company_website':
      if (!v) return '';
      if (!URL_RE.test(v)) return 'Enter a valid website URL (e.g. https://example.com).';
      return '';
    case 'status':
      if (!v) return 'Please select a status.';
      if (!STATUS_VALUES.includes(v)) return 'Invalid status.';
      return '';
    default:
      return '';
  }
};

const collectValidationErrors = (form, { hasSelectedCompany, isPartnershipStaff }) => {
  const errors = {};
  ['wanted_item', 'amount', 'purpose'].forEach((key) => {
    const msg = validateRequirementField(key, form[key]);
    if (msg) errors[key] = msg;
  });
  if (!hasSelectedCompany) {
    MANUAL_COMPANY_FIELDS.forEach((key) => {
      const msg = validateRequirementField(key, form[key]);
      if (msg) errors[key] = msg;
    });
  }
  if (isPartnershipStaff) {
    const msg = validateRequirementField('status', form.status);
    if (msg) errors.status = msg;
  }
  return errors;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const RequirementForm = ({ isEdit = false }) => {
  const navigate              = useNavigate();
  const { id }                = useParams();
  const { user: currentUser } = useAuth();

  const [loading,        setLoading]        = useState(false);
  const [dataLoading,    setDataLoading]    = useState(isEdit);
  const [error,          setError]          = useState(null);
  const [success,        setSuccess]        = useState(null);
  const [confirmOpen,    setConfirmOpen]    = useState(false);
  const [users,          setUsers]          = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [showFieldErrors, setShowFieldErrors] = useState(false);

  const isPartnershipStaff = canAccessPartnershipManagement(currentUser);

  const [formData, setFormData] = useState({
    wanted_item:           '',
    amount:                '',
    purpose:               '',
    company_id:            '',
    company_name:          '',
    company_address:       '',
    company_website:       '',
    company_contact:       '',
    company_contact_email: '',
    is_emergency:          false,
    user_ids:              [],
    status:                'not_started',
  });

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'requirement',
    userId: currentUser?.id,
    isEditMode: isEdit,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    loadAuxData();
    if (isEdit && id) fetchRequirement();
    else setDataLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, id, isEdit]);

  const loadAuxData = async () => {
    try {
      const requests = [api.get('/companies/for-claim-form')];
      if (isPartnershipStaff) requests.push(api.get('/users?active_only=1'));
      const [companiesRes, usersRes] = await Promise.all(requests);
      const companies = companiesRes.data;
      setCompanyOptions(
        Array.isArray(companies) ? companies : (companies?.data && Array.isArray(companies.data) ? companies.data : []),
      );
      if (isPartnershipStaff && usersRes)
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch {
      setCompanyOptions([]);
      setUsers([]);
    }
  };

  const fetchRequirement = async () => {
    try {
      setDataLoading(true);
      const { data: r } = await api.get(`/requirements/${id}`);
      if (!canEditRequirement(currentUser, r)) {
        setError(
          isRequirementOwner(currentUser, r)
            ? 'You can only edit your requirement while it is not started.'
            : 'You are not authorized to edit this requirement.',
        );
        navigate('/requirements', { replace: true });
        return;
      }
      setFormData({
        wanted_item:           r.wanted_item || '',
        amount:                r.amount != null ? String(r.amount) : '',
        purpose:               r.purpose || '',
        company_id:            r.company_id != null
          ? String(r.company_id)
          : (r.company_name ? CUSTOM_COMPANY_VALUE : ''),
        company_name:          r.company_name || '',
        company_address:       r.company_address || '',
        company_website:       r.company_website || '',
        company_contact:       r.company_contact || '',
        company_contact_email: r.company_contact_email || '',
        is_emergency:          Boolean(r.is_emergency),
        user_ids:              (r.assignees || []).map((member) => String(member.id)),
        status:                r.status || 'not_started',
      });
      setValidationErrors({});
    } catch {
      setError('Failed to load requirement.');
      navigate('/requirements');
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleCompanySelect = (val) => {
    if (val === CUSTOM_COMPANY_VALUE) {
      setFormData((prev) => ({
        ...prev,
        company_id: CUSTOM_COMPANY_VALUE,
        company_name: prev.company_id === CUSTOM_COMPANY_VALUE ? prev.company_name : '',
        company_address: prev.company_id === CUSTOM_COMPANY_VALUE ? prev.company_address : '',
        company_website: prev.company_id === CUSTOM_COMPANY_VALUE ? prev.company_website : '',
        company_contact: prev.company_id === CUSTOM_COMPANY_VALUE ? prev.company_contact : '',
        company_contact_email: prev.company_id === CUSTOM_COMPANY_VALUE ? prev.company_contact_email : '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        company_id: val,
        company_name: '',
        company_address: '',
        company_website: '',
        company_contact: '',
        company_contact_email: '',
      }));
      if (showFieldErrors) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          MANUAL_COMPANY_FIELDS.forEach((k) => {
            if (next[k]) next[k] = '';
          });
          return next;
        });
      }
    }
    if (error) setError(null);
    if (showFieldErrors && validationErrors.company_id) {
      setValidationErrors((prev) => ({ ...prev, company_id: '' }));
    }
  };

  const handleNativeChange = (e) => handleChange(e.target.name, e.target.value);

  const buildPayload = () => {
    const isCustomCompany = formData.company_id === CUSTOM_COMPANY_VALUE;
    const cid = !isCustomCompany && formData.company_id
      ? parseInt(formData.company_id, 10)
      : null;
    const payload = {
      wanted_item:  formData.wanted_item.trim(),
      amount:       formData.amount.trim(),
      purpose:      formData.purpose.trim(),
      company_id:   cid,
      is_emergency: formData.is_emergency,
    };
    if (!cid) {
      payload.company_name          = formData.company_name.trim();
      payload.company_address       = formData.company_address.trim();
      payload.company_website       = formData.company_website?.trim() || null;
      payload.company_contact       = formData.company_contact.trim();
      payload.company_contact_email = formData.company_contact_email?.trim() || null;
    }
    if (isPartnershipStaff) {
      payload.user_ids = (formData.user_ids || []).map((userId) => parseInt(userId, 10));
      payload.status         = formData.status;
    }
    return payload;
  };

  const validateForm = () => {
    const errors = collectValidationErrors(formData, {
      hasSelectedCompany: Boolean(formData.company_id && formData.company_id !== CUSTOM_COMPANY_VALUE),
      isPartnershipStaff,
    });
    setValidationErrors(errors);
    setShowFieldErrors(true);
    if (Object.keys(errors).length > 0) {
      setError(formatFieldErrorsMessage(errors));
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit) {
      setError(null);
      setValidationErrors({});
      setShowFieldErrors(false);
      setConfirmOpen(true);
      return;
    }
    if (!validateForm()) return;
    doSave();
  };

  const handleConfirmUpdate = () => {
    const errors = collectValidationErrors(formData, {
      hasSelectedCompany: Boolean(formData.company_id && formData.company_id !== CUSTOM_COMPANY_VALUE),
      isPartnershipStaff,
    });
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen: setConfirmOpen,
        setShowFieldErrors,
      });
      return;
    }
    setConfirmOpen(false);
    doSave();
  };

  const doSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});
      const payload = buildPayload();
      if (isEdit) await api.put(`/requirements/${id}`, payload);
      else        await api.post('/requirements', payload);
      if (!isEdit) formDraft.clearDraft();
      setSuccess(`Requirement ${isEdit ? 'updated' : 'created'} successfully!`);
      try { localStorage.setItem('requirementListView', 'table'); } catch { /* ignore */ }
      setTimeout(() => navigate('/requirements', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      const data = err?.response?.data;
      applyApiValidationErrors(data, setValidationErrors, API_VALIDATION_FIELDS);
      const fieldMsgs = {};
      const errs = data?.errors;
      if (errs && typeof errs === 'object') {
        Object.entries(errs).forEach(([key, msgs]) => {
          if (!API_VALIDATION_FIELDS.has(key)) return;
          const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
          if (msg) fieldMsgs[key] = String(msg);
        });
      }
      if (Object.keys(fieldMsgs).length) setShowFieldErrors(true);
      setError(
        Object.keys(fieldMsgs).length
          ? formatFieldErrorsMessage(fieldMsgs)
          : (getFirstErrorMessage(data) || `Failed to ${isEdit ? 'update' : 'create'} requirement.`),
      );
    } finally { setLoading(false); }
  };

  const companySelectOptions = useMemo(() => [
    ...companyOptions.map((c) => ({ value: String(c.id), label: c.name })),
    { value: CUSTOM_COMPANY_VALUE, label: 'Enter a custom name…' },
  ], [companyOptions]);

  if (!currentUser) return null;
  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading requirement form" size={56} variant="primary" />
    </Box>
  );

  const hasLinkedCompany = Boolean(formData.company_id && formData.company_id !== CUSTOM_COMPANY_VALUE);
  const isCustomCompany = formData.company_id === CUSTOM_COMPANY_VALUE;

  const manualCompanyFilled = Boolean(
    formData.company_name?.trim()
    && formData.company_address?.trim()
    && formData.company_contact?.trim()
    && formData.company_contact_email?.trim(),
  );

  const companySatisfied = hasLinkedCompany || (isCustomCompany && manualCompanyFilled);

  const canSubmit = Boolean(
    formData.wanted_item?.trim()
    && formData.amount?.trim()
    && formData.purpose?.trim()
    && companySatisfied,
  );
  const submitDisabled = loading || !canSubmit;
  const { fieldHelper, fieldHasError } = createFieldErrorGate(showFieldErrors, validationErrors);

  const userSelectOptions = [
    ...users.map(u => ({ value: String(u.id), label: u.name || u.email || `User #${u.id}` })),
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>
      <Header
        title={isEdit ? 'Edit Requirement' : 'Create New Requirement'}
        subtitle={isEdit ? 'Update requirement details' : 'Submit a partnership or project requirement'}
        icon={RequirementIcon}
        showBackButton onBackClick={() => navigate(-1)} backButtonText="Back to Requirements"
      />

      <FeedbackMessage
        message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />
      <FeedbackMessage
        message={success} severity="success" onClose={() => setSuccess(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_MID, 0.95)})`, border: `1px solid ${alpha(NEON3, 0.35)}`, boxShadow: `0 4px 20px ${alpha(NEON3, 0.15)}`, color: alpha(NEON3, 0.95) }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ── 1. Request ─────────────────────────────────────────────── */}
          <SectionCard title="Request" icon={<RequirementIcon />} delay={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                <F fullWidth required label="Item / resource" name="wanted_item" value={formData.wanted_item} onChange={handleNativeChange}
                  error={fieldHasError('wanted_item')} helperText={fieldHelper('wanted_item')}
                  InputProps={{ startAdornment: iconAdorn(NameIcon) }} />
                <F fullWidth required label="Amount / quantity" name="amount" value={formData.amount} onChange={handleNativeChange}
                  error={fieldHasError('amount')} helperText={fieldHelper('amount')}
                  InputProps={{ startAdornment: iconAdorn(NotesIcon) }} />
              </Box>
              <F fullWidth required label="Purpose" name="purpose" value={formData.purpose} onChange={handleNativeChange} multiline rows={3}
                error={fieldHasError('purpose')} helperText={fieldHelper('purpose')}
                InputProps={{ startAdornment: topAdorn(DescriptionIcon) }} />
              <Box
                onClick={() => handleChange('is_emergency', !formData.is_emergency)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderRadius: '10px',
                  border: `1px solid ${formData.is_emergency ? alpha(DANGER, 0.35) : alpha(ACCENT, 0.12)}`,
                  background: formData.is_emergency ? alpha(DANGER, 0.06) : alpha(SURFACE_UP, 0.5),
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                  '&:hover': {
                    border: `1px solid ${formData.is_emergency ? alpha(DANGER, 0.5) : alpha(ACCENT, 0.25)}`,
                    background: formData.is_emergency ? alpha(DANGER, 0.09) : alpha(SURFACE_UP, 0.8),
                  },
                }}
              >
                {/* Icon */}
                <WarningIcon
                  sx={{
                    fontSize: 18,
                    color: formData.is_emergency ? alpha(DANGER, 0.9) : alpha(TEXT_SECONDARY, 0.35),
                    transition: 'color 0.2s ease',
                    flexShrink: 0,
                  }}
                />

                {/* Text */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: formData.is_emergency ? alpha(DANGER, 0.95) : alpha(TEXT_PRIMARY, 0.7),
                      transition: 'color 0.2s ease',
                      lineHeight: 1.3,
                    }}
                  >
                    Mark as urgent / emergency
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '0.75rem',
                      color: formData.is_emergency ? alpha(DANGER, 0.55) : alpha(TEXT_SECONDARY, 0.45),
                      transition: 'color 0.2s ease',
                      mt: 0.25,
                    }}
                  >
                    Flag this request as time-sensitive
                  </Typography>
                </Box>

                {/* Switch */}
                <Box
                  sx={{
                    width: 36,
                    height: 20,
                    borderRadius: '99px',
                    background: formData.is_emergency ? alpha(DANGER, 0.85) : alpha(TEXT_SECONDARY, 0.18),
                    position: 'relative',
                    flexShrink: 0,
                    transition: 'background 0.2s ease',
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: '#fff',
                      position: 'absolute',
                      top: 3,
                      left: formData.is_emergency ? 19 : 3,
                      transition: 'left 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </SectionCard>

          {/* ── 2. Related Company ─────────────────────────────────────── */}
          <SectionCard title="Related Company" icon={<BusinessIcon />} delay={0.06}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              <SearchableSelect
                required
                label="Company"
                value={formData.company_id}
                onChange={handleCompanySelect}
                options={companySelectOptions}
                error={fieldHasError('company_id')}
                helperText={fieldHelper('company_id')}
                startIcon={<BusinessIcon sx={{ color: alpha(ACCENT, 0.4), fontSize: 18 }} />}
              />

              <AnimatePresence initial={false}>
                {isCustomCompany && (
                  <motion.div
                    key="manual-company"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                        <F fullWidth required label="Company name" name="company_name" value={formData.company_name} onChange={handleNativeChange}
                          error={fieldHasError('company_name')} helperText={fieldHelper('company_name')}
                          InputProps={{ startAdornment: iconAdorn(BusinessIcon) }} />
                        <F fullWidth label="Website" name="company_website" value={formData.company_website} onChange={handleNativeChange}
                          error={fieldHasError('company_website')} helperText={fieldHelper('company_website')}
                          InputProps={{ startAdornment: iconAdorn(LinkIcon) }} />
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                        <F fullWidth required label="Contact number" name="company_contact" value={formData.company_contact} onChange={handleNativeChange}
                          error={fieldHasError('company_contact')} helperText={fieldHelper('company_contact')}
                          InputProps={{ startAdornment: iconAdorn(PhoneIcon) }} />
                        <F fullWidth required label="Contact email" name="company_contact_email" value={formData.company_contact_email} onChange={handleNativeChange} type="email"
                          error={fieldHasError('company_contact_email')} helperText={fieldHelper('company_contact_email')}
                          InputProps={{ startAdornment: iconAdorn(EmailIcon) }} />
                      </Box>
                      <F fullWidth required label="Address" name="company_address" value={formData.company_address} onChange={handleNativeChange} multiline rows={2}
                        error={fieldHasError('company_address')} helperText={fieldHelper('company_address')}
                        InputProps={{ startAdornment: topAdorn(LocationIcon) }} />
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>

            </Box>
          </SectionCard>

          {/* ── 3. Partnership Desk (staff only) ──────────────────────── */}
          {isPartnershipStaff && (
            <SectionCard title="Partnership Desk" icon={<PersonIcon />} delay={0.12}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                <SearchableSelect
                  multiple
                  label="Responsible members"
                  value={formData.user_ids}
                  onChange={(v) => handleChange('user_ids', v)}
                  options={userSelectOptions}
                  error={fieldHasError('user_ids')}
                  helperText={fieldHelper('user_ids')}
                  stackedTags
                  startIcon={<PersonIcon sx={{ color: alpha(ACCENT, 0.4), fontSize: 18 }} />}
                />
                <SearchableSelect
                  required
                  label="Status"
                  value={formData.status}
                  onChange={(v) => handleChange('status', v)}
                  options={STATUS_OPTIONS}
                  error={fieldHasError('status')}
                  helperText={fieldHelper('status')}
                  startIcon={<FlagIcon sx={{ color: alpha(ACCENT, 0.4), fontSize: 18 }} />}
                />
              </Box>
            </SectionCard>
          )}

          <SubmitActionsCard
            statusText={isEdit ? 'Save changes to update this requirement.' : 'Complete the form to add a new requirement.'}
            missingRequiredFields={isEdit ? null : [!formData.wanted_item?.trim() && 'Item / Resource', !formData.amount?.trim() && 'Amount / Quantity', !formData.purpose?.trim() && 'Purpose', !hasLinkedCompany && !isCustomCompany && 'Company', isCustomCompany && !formData.company_name?.trim() && 'Company Name', isCustomCompany && !formData.company_address?.trim() && 'Address', isCustomCompany && !formData.company_contact?.trim() && 'Contact Number', isCustomCompany && !formData.company_contact_email?.trim() && 'Contact Email'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to create requirement."
            cancelLabel="Cancel" onCancel={() => navigate(-1)} cancelDisabled={loading}
            submitLabel={isEdit ? 'Update Requirement' : 'Create Requirement'}
            submitDisabled={submitDisabled}
            loading={loading}
            submitStartIcon={<SaveIcon />}
            showSubmit
            submitType="submit"
            {...submitDraftProps}
          />

        </Box>
      </form>

      <ConfirmDeleteDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmUpdate}
        loading={loading} variant="edit" title="Confirm Update"
        itemType="requirement" itemName={formData.wanted_item}
        description="Please review your changes before saving. This will overwrite the existing requirement record."
        confirmLabel="Update Requirement" confirmColor="primary"
        confirmDisabled={!canSubmit}
      />
    </Container>
  );
};

export default RequirementForm;
